// A project agent's Runs by where the run came from (ADR 0039 §4), as its Used in is by what uses it: an app's
// ctx.agents.ask, a workflow step, the chat and the evaluations. The asks outside the chat are the agents API's
// record (…/agents/:key/asks); the chat's runs are the host's ledger (its runs port) less the evaluations' sessions;
// the evaluations are the host's evals port.
import type { AgentAsks } from '@cortex/sdk-agents-client';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import type { EvalRun } from './Evaluations.tsx';
import { agentRunsKey, useAgentsHost, useAgentsQuery, useAgentsKey } from './host.tsx';
import type { LedgerRun } from './model.ts';
import { OutcomeBadge } from './RecentRuns.tsx';
import { unwrap } from './ui.tsx';

type Ask = AgentAsks['asks'][number];

const ASKS = '/agents/{key}/asks' as const;

/** How many evaluation runs' sessions are read to tell their cases from the chat. */
const EVAL_RUNS_READ = 5;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const when = (iso: string) => new Date(iso).toLocaleString();
const secs = (ms: number) =>
  ms >= 60_000 ? `${Math.round(ms / 60_000)} m` : `${Math.max(1, Math.round(ms / 1000))} s`;

function Section({
  id,
  title,
  count,
  empty,
  children,
}: {
  id: string;
  title: string;
  count: number;
  empty: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="mb-4" data-testid={`agent-runs-${id}`}>
      <h3
        className={
          'text-sm m-0 mb-1.5 flex items-center gap-1.5 ' +
          (count ? 'font-semibold text-page-title' : 'font-normal text-page-muted')
        }
      >
        {title} <span className="font-normal text-page-muted">{count}</span>
      </h3>
      {count ? (
        <ul className="m-0 p-0 list-none rounded-md border border-card-border divide-y divide-divider">
          {children}
        </ul>
      ) : (
        <div className="text-xs text-page-muted">{empty}</div>
      )}
    </section>
  );
}

function Row({
  outcome,
  status,
  who,
  task,
  at,
  extra,
}: {
  outcome: string | null | undefined;
  status: string;
  who?: React.ReactNode;
  task: string;
  at: string;
  extra?: React.ReactNode;
}) {
  return (
    <li className="px-3 py-2 text-xs flex items-center gap-2 min-w-0" data-testid="agent-run">
      {outcome ? <OutcomeBadge outcome={outcome} /> : <span className="text-page-muted">{status}</span>}
      {who ? <span className="font-medium text-page-text whitespace-nowrap">{who}</span> : null}
      <span className="truncate text-page-text" title={task}>
        {task.slice(0, 160)}
      </span>
      <span className="ml-auto whitespace-nowrap text-page-muted">
        {extra ? <>{extra} · </> : null}
        {when(at)}
      </span>
    </li>
  );
}

const askExtra = (a: Ask) =>
  `${secs(a.durationMs)} · ${(a.inputTokens + a.outputTokens).toLocaleString()} tokens`;
/** A chat run's time and tokens, as an ask's (the runs recorded before them have none). */
const ledgerExtra = (r: LedgerRun) =>
  r.durationMs != null && r.inputTokens != null
    ? `${secs(r.durationMs)} · ${(r.inputTokens + (r.outputTokens ?? 0)).toLocaleString()} tokens`
    : undefined;

/** An agent's evaluation runs, and the chat sessions of the newest runs' cases (their ledger runs are the tests'). */
function useEvalRuns(subject: string) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const q = useAgentsQuery(
    k('asks-evals', subject),
    async () => {
      const runs = await host.evals!.runs({ subject });
      const details = await Promise.all(
        runs.slice(0, EVAL_RUNS_READ).map((r) => host.evals!.run({ id: r.id }).catch(() => null)),
      );
      const sessions = new Set(
        details.flatMap((d) =>
          (d?.results ?? []).map((x) => x.trace?.session_id).filter((s): s is string => !!s),
        ),
      );
      return { runs, sessions };
    },
    { enabled: !!host.evals },
  );
  return { runs: q.data?.runs ?? ([] as EvalRun[]), sessions: q.data?.sessions ?? new Set<string>() };
}

/** The Tests section: the agent's evaluation runs, each opening its Evaluations tab. */
function TestsSection({ runs, to }: { runs: EvalRun[]; to: string }) {
  return (
    <Section id="tests" title="Tests" count={runs.length} empty="No evaluation runs yet (Evaluations).">
      {runs.map((r) => (
        <li key={r.id} className="text-xs" data-testid="agent-run">
          <Link
            to={to}
            className="px-3 py-2 flex items-center gap-2 no-underline text-inherit hover:bg-card-border/30"
          >
            <span
              className={
                'rounded px-1.5 py-0.5 text-[11px] font-medium ' +
                (r.status === 'passed'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : r.status === 'running'
                    ? 'bg-card-border/40 text-page-muted'
                    : 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300')
              }
            >
              {r.status}
            </span>
            <span className="font-medium text-page-text">run #{r.id}</span>
            <span className="text-page-muted">
              {r.passed}/{r.total || r.passed + r.failed + r.errors} passed
            </span>
            <span className="ml-auto whitespace-nowrap text-page-muted">{when(r.startedAt)} ›</span>
          </Link>
        </li>
      ))}
    </Section>
  );
}

/** The summary line over the sections: how many runs, how many evaluation runs. */
function Summary({ runs, tests, error }: { runs: number; tests: number; error?: string | null }) {
  return (
    <div className="text-sm text-page-muted mb-3" data-testid="agent-runs-summary">
      {runs + tests ? (
        <>
          <span className="text-page-text font-medium">{plural(runs, 'run')}</span> and{' '}
          <span className="text-page-text font-medium">{plural(tests, 'evaluation run')}</span>
          {error ? <span className="text-red-600"> · {error}</span> : null}
        </>
      ) : (
        'No runs yet.'
      )}
    </div>
  );
}

export function RunsBySource({ agent }: { agent: string }) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const asksQ = useAgentsQuery(k('asks', agent), () =>
    unwrap(host.client.fetch.GET(ASKS, { params: { path: { key: agent } } })),
  );
  const ledgerQ = useAgentsQuery(
    agentRunsKey(host.cache.runs, `agent-${agent}`, 'recent'),
    () => host.runs!({ persona: `agent-${agent}`, all: true, limit: 50 }),
    { enabled: !!host.runs },
  );
  const evals = useEvalRuns(`agent:${agent}`);
  if ((asksQ.loading && !asksQ.data) || (ledgerQ.loading && !ledgerQ.data))
    return (
      <div className="flex items-center gap-2 text-xs text-page-muted">
        <Loader2 size={12} className="animate-spin" /> Loading runs…
      </div>
    );
  const asks = asksQ.data?.asks ?? [];
  const recorded = asksQ.data?.recorded ?? false;
  const fromApps = asks.filter((a) => a.source === 'app');
  const fromWorkflows = asks.filter((a) => a.source === 'workflow');
  const fromApi = asks.filter((a) => a.source === 'api');
  const chat = ((ledgerQ.data ?? []) as LedgerRun[]).filter(
    (r) => !r.sessionId || !evals.sessions.has(r.sessionId),
  );
  const evalRuns = evals.runs;
  const notRecorded = 'Not recorded on this host.';
  return (
    <div data-testid="agent-runs-by-source">
      <Summary
        runs={fromApps.length + fromWorkflows.length + fromApi.length + chat.length}
        tests={evalRuns.length}
        error={asksQ.error ?? ledgerQ.error}
      />
      <Section
        id="apps"
        title="From apps"
        count={fromApps.length}
        empty={recorded ? 'No app has asked it (ctx.agents.ask) yet.' : notRecorded}
      >
        {fromApps.map((a) => (
          <Row
            key={a.id}
            outcome={a.outcome}
            status={a.status}
            who={a.app}
            task={a.message}
            at={a.startedAt}
            extra={askExtra(a)}
          />
        ))}
      </Section>
      <Section
        id="workflows"
        title="From workflows"
        count={fromWorkflows.length}
        empty={recorded ? 'No workflow step has run it yet.' : notRecorded}
      >
        {fromWorkflows.map((a) => (
          <Row
            key={a.id}
            outcome={a.outcome}
            status={a.status}
            who={`step ${a.workflow?.step ?? '?'}`}
            task={a.workflow?.instanceId ? `run ${a.workflow.instanceId}` : a.message}
            at={a.startedAt}
            extra={askExtra(a)}
          />
        ))}
      </Section>
      {fromApi.length ? (
        <Section id="api" title="From the API" count={fromApi.length} empty="">
          {fromApi.map((a) => (
            <Row
              key={a.id}
              outcome={a.outcome}
              status={a.status}
              task={a.message}
              at={a.startedAt}
              extra={askExtra(a)}
            />
          ))}
        </Section>
      ) : null}
      <Section
        id="chat"
        title="Chat"
        count={chat.length}
        empty="Nobody has chatted with it yet (Chat with agent)."
      >
        {chat.map((r) => (
          <Row
            key={r.id}
            outcome={r.outcome}
            status={r.status}
            task={r.task}
            at={r.startedAt}
            extra={ledgerExtra(r)}
          />
        ))}
      </Section>
      <TestsSection runs={evalRuns} to={host.paths.agent(agent, 'tab=evaluations')} />
    </div>
  );
}

/**
 * A platform agent's Runs, as a project agent's: its runs in this project by where they came from — asked in the
 * host's chat, or handed to it by the host's own agent (a run with a parent) — and its tests. A platform agent is not in apps, so it
 * has no runs from apps, workflows or the API.
 */
export function PlatformRunsBySource({ persona }: { persona: string }) {
  const host = useAgentsHost();
  const ledgerQ = useAgentsQuery(
    agentRunsKey(host.cache.runs, persona, 'recent'),
    () => host.runs!({ persona, all: true, limit: 50 }),
    { enabled: !!host.runs },
  );
  const evals = useEvalRuns(`persona:${persona}`);
  if (!host.runs) return null;
  if (ledgerQ.loading && !ledgerQ.data)
    return (
      <div className="flex items-center gap-2 text-xs text-page-muted">
        <Loader2 size={12} className="animate-spin" /> Loading runs…
      </div>
    );
  const runs = ((ledgerQ.data ?? []) as LedgerRun[]).filter(
    (r) => !r.sessionId || !evals.sessions.has(r.sessionId),
  );
  const asked = runs.filter((r) => r.parentRunId == null);
  const handed = runs.filter((r) => r.parentRunId != null);
  const row = (r: LedgerRun) => (
    <Row
      key={r.id}
      outcome={r.outcome}
      status={r.status}
      task={r.task}
      at={r.startedAt}
      extra={ledgerExtra(r)}
    />
  );
  return (
    <div data-testid="agent-runs-by-source">
      <Summary runs={runs.length} tests={evals.runs.length} error={ledgerQ.error} />
      <Section
        id="chat"
        title="Chat"
        count={asked.length}
        empty={`Nobody has asked it in ${host.platformChat ?? 'the host’s chat'} yet.`}
      >
        {asked.map(row)}
      </Section>
      {persona === 'orchestrator' ? null : (
        <Section
          id="from-platform"
          title={`From ${host.platform ?? 'the host'}`}
          count={handed.length}
          empty={`${host.platform ?? 'The host'} has not handed it a question yet.`}
        >
          {handed.map(row)}
        </Section>
      )}
      <TestsSection runs={evals.runs} to={`${host.paths.agents()}?persona=${persona}&tab=evaluations`} />
    </div>
  );
}
